import mapKeys from 'lodash/mapKeys';
import pickBy from 'lodash/pickBy';
import snakeCase from 'lodash/snakeCase';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

/**
 * Builds a flat object of route param keys prefixed with route_param_ and snake_cased,
 * for use as PostHog super properties.
 */
function buildRouteParamProperties(
  params: Record<string, string | undefined> | undefined,
): Record<string, string> {
  if (!params || Object.keys(params).length === 0) {
    return {};
  }
  const defined = pickBy(params, (v): v is string => v !== undefined);
  return mapKeys(defined, (_, key) => `route_param_${snakeCase(key)}`) as Record<string, string>;
}

/**
 * Runs PostHog pageview tracking and super-property registration when a route matches.
 * Uses useRouteMatch() for the path template and params, and useLocation() so the effect re-runs on navigation.
 */
export function PostHogPageviewTracker() {
  const posthog = usePostHog();
  const location = useLocation();
  const match = useRouteMatch();

  useEffect(() => {
    if (!posthog || !match) return;

    const routeParamProperties = buildRouteParamProperties(match.params);

    posthog.register({
      route_path: match.path,
      ...routeParamProperties,
    });

    posthog.capture('$pageview');

    return () => {
      posthog.unregister('route_path');
      Object.keys(routeParamProperties).forEach((key) => posthog.unregister(key));
    };
  }, [posthog, location.pathname, match?.path, match?.params]);

  return null;
}
